import {
  BlockType,
  ChoiceFieldType,
  InstructionsFieldType,
  TableFieldType,
} from "../../../shared/types/worksheet";
import { createId } from "../../../shared/utils";
import { createBlockOfType } from "../../../shared/worksheets/doc-helpers";
import { markdownToDelta } from "../../../worksheets/helpers/markdown-to-delta";
import { AIPseudoBlockType } from "../schemas/worksheet-schema";

import { rollbarAndLogError } from "utils/logger";

/**
 * Take a list of pseudo blocks generated by AI and convert them to actual worksheet blocks.
 * This involves adding metadata, changing the field format, and combining instructions fields
 * into blocks where appropriate.
 *
 * Example input:
 * [
 *  {block_type: "instructions", block_data: "# Hello"},
 *  {block_type: "free_response", block_data: "What is the capital of France?"},
 *  {block_type: "multiple_choice_single_answer", block_data: ["Paris", "London", "Berlin"]},
 *  {block_type: "instructions", block_data: "# Goodbye"}
 * ]
 *
 * Example output:
 * [
 *  {t: "FreeResponse", f: [
 *    {t: "Text", v: markdownToDelta("What is the capital of France?").ops, "ver": 1, id: "instructions-1"},
 *    {t: "RichTextField", v: {ops: []}, "ver": 1, id: "content-1"}
 *  ]},
 *  {t: "MultipleChoice", f: [
 *    {t: "Text", v: {ops: []}, "ver": 1, id: "instructions-2"},
 *    {t: "ChoiceField", m: false, "ver": 1, id: "content-2", c: [
 *      {id: "choice-2-0", l: "Paris", v: "0"},
 *      {id: "choice-2-1", l: "London", v: "1"},
 *      {id: "choice-2-2", l: "Berlin", v: "2"},
 *    ]},
 *  ]},
 *  {t: "Instructions", f: [
 *    {t: "Text", v: markdownToDelta("# Goodbye").ops, "ver": 1, id: "instructions-3"}
 *  ]}
 * ]
 *
 * Note that the MultipleChoice block has an empty Text field for instructions, since the pseudo block
 * format doesn't distinguish between blocks and fields, so doesn't know that some blocks have built-in
 * instructions that need to be filled.
 */
export const pseudoBlocksToWorksheetBlocks = (blocks: AIPseudoBlockType[]): BlockType[] => {
  const result: BlockType[] = [];

  const pseudoBlockTypeToBlockType: Record<AIPseudoBlockType["block_type"], BlockType["t"]> = {
    free_response: "FreeResponse",
    multiple_choice_single_answer: "MultipleChoice",
    multiple_choice_multiple_answers: "MultipleChoice",
    table: "Table",
    file_upload: "File",
    whiteboard: "Whiteboard",
  };

  const blocksThatHaveInstructions: BlockType["t"][] = ["FreeResponse", "MultipleChoice", "File"];

  const createBlock = (blockInput: AIPseudoBlockType) => {
    const normalizedType = blockInput?.block_type;
    const generatedBlock = createBlockOfType(pseudoBlockTypeToBlockType[normalizedType]);
    const instructions: string | undefined =
      typeof blockInput?.instructions === "string" ? blockInput.instructions : undefined;

    // If the block supports an instructions field and the input provides instructions, set it
    if (blocksThatHaveInstructions.includes(generatedBlock.t) && instructions) {
      const textField = generatedBlock.f[0] as InstructionsFieldType;
      textField.v = markdownToDelta(instructions).ops;
    }

    switch (normalizedType) {
      case "multiple_choice_single_answer": {
        const choiceField = generatedBlock.f[1] as ChoiceFieldType;
        const data: unknown = blockInput?.data;
        const choices = Array.isArray(data)
          ? (data as string[]).map((choice, i) => ({
              id: createId(),
              l: choice,
              v: i.toString(),
            }))
          : [];
        choiceField.m = false;
        choiceField.c = choices;
        break;
      }
      case "multiple_choice_multiple_answers": {
        const choiceField = generatedBlock.f[1] as ChoiceFieldType;
        const data: unknown = blockInput?.data;
        const choices = Array.isArray(data)
          ? (data as string[]).map((choice, i) => ({
              id: createId(),
              l: choice,
              v: i.toString(),
            }))
          : [];
        choiceField.m = true;
        choiceField.c = choices;
        break;
      }
      case "table": {
        // Table blocks don't have built-in instructions; if provided, prepend a standalone Instructions block
        if (instructions) {
          result.push({
            id: createId(),
            t: "Instructions",
            f: [
              {
                t: "Text",
                id: createId(),
                ver: 1,
                v: markdownToDelta(instructions).ops,
              },
            ],
          });
        }
        const tableField = generatedBlock.f[0] as TableFieldType;
        const data: unknown = blockInput?.data;
        const is2DArray =
          Array.isArray(data) && data.length > 0 && Array.isArray((data as unknown[])[0]);
        const matrix: string[][] = is2DArray ? (data as string[][]) : [[""]];
        const rows = matrix.length;
        const cols = matrix[0].length;
        tableField.v = {
          r: Array.from({ length: rows }, () => createId()),
          c: Array.from({ length: cols }, () => createId()),
          cm: {},
        };
        matrix.forEach((row, rowIndex) => {
          row.forEach((cell, colIndex) => {
            if (cell) {
              const cellId = `${tableField.v.r[rowIndex]},${tableField.v.c[colIndex]}`;
              tableField.v.cm[cellId] = {
                v: markdownToDelta(cell).ops,
              };
            }
          });
        });
        break;
      }
      case "file_upload":
      case "free_response":
      case "whiteboard": {
        // Whiteboard blocks don't have built-in instructions; if provided, prepend a standalone Instructions block
        if (normalizedType === "whiteboard" && instructions) {
          result.push({
            id: createId(),
            t: "Instructions",
            f: [
              {
                t: "Text",
                id: createId(),
                ver: 1,
                v: markdownToDelta(instructions).ops,
              },
            ],
          });
        }
        // Nothing else to do beyond optional instructions above
        break;
      }
      default: {
        break;
      }
    }

    result.push(generatedBlock);
  };

  blocks.forEach((block) => {
    try {
      createBlock(block);
    } catch (e) {
      rollbarAndLogError("AI failed to generate valid block", e, {
        block,
      });
    }
  });
  // We expect always at least as many real blocks as pseudo blocks.
  // We can't compare exact count, since some pseudo blocks may result in multiple real blocks.
  if (blocks.length > result.length) {
    throw new Error("Pseudo-blocks could not be converted to worksheet blocks");
  }

  return result;
};
